import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Avatar,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    CameraAlt as CameraIcon,
    Image as ImageIcon,
} from "@mui/icons-material";
import { BarcodeScannerDialog } from "../common/BarcodeScannerDialog";
import { productService } from "../../services/productService";
import { Product } from "../../types";
import { ProductImage } from "../../types/productImage";

interface QuickBarcodeDialogProps {
    open: boolean;
    onClose: () => void;
    product: Product | null;
    /** Called with the updated product after a successful save. */
    onSaved: (updated: Product) => void;
}

// A minimal, single-field shortcut for the barcode field buried inside the
// full ProductForm — built for the common shelf/receiving workflow of
// scanning a freshly-printed label onto a product that doesn't have one
// yet, without navigating the whole edit form on a phone.
export const QuickBarcodeDialog: React.FC<QuickBarcodeDialogProps> = ({
    open,
    onClose,
    product,
    onSaved,
}) => {
    const [barcode, setBarcode] = useState("");
    const [scannerOpen, setScannerOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open && product) {
            setBarcode(product.barcode || "");
            setError("");
        }
    }, [open, product]);

    const handleClose = () => {
        if (saving) return;
        onClose();
    };

    const handleSave = async () => {
        if (!product) return;
        setSaving(true);
        setError("");
        try {
            const updated = await productService.updateProduct(
                product.product_id,
                { barcode: barcode.trim() },
            );
            onSaved(updated);
            onClose();
        } catch (err: any) {
            setError(
                err?.response?.data?.detail || "Failed to update barcode",
            );
        } finally {
            setSaving(false);
        }
    };

    if (!product) return null;

    const images = (product.images || []) as ProductImage[];
    const defaultImage = images.find((img) => img.is_default) || images[0];

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Barcode</DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mb: 2,
                        }}
                    >
                        {defaultImage ? (
                            <Avatar
                                src={defaultImage.thumbnail_url}
                                alt={product.name}
                                variant="rounded"
                                sx={{ width: 44, height: 44, flexShrink: 0 }}
                            />
                        ) : (
                            <Avatar
                                variant="rounded"
                                sx={{
                                    width: 44,
                                    height: 44,
                                    flexShrink: 0,
                                    bgcolor: "grey.200",
                                }}
                            >
                                <ImageIcon sx={{ color: "grey.400" }} />
                            </Avatar>
                        )}
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="body1"
                                fontWeight="bold"
                                noWrap
                            >
                                {product.name}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                SKU: {product.sku}
                            </Typography>
                        </Box>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <TextField
                        autoFocus
                        fullWidth
                        label="Barcode"
                        placeholder="e.g. 8901234567890"
                        value={barcode}
                        onChange={(e) => setBarcode(e.target.value)}
                        disabled={saving}
                        helperText="Leave blank to remove the barcode"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title="Scan with camera">
                                        <span>
                                            <IconButton
                                                onClick={() =>
                                                    setScannerOpen(true)
                                                }
                                                edge="end"
                                                disabled={saving}
                                            >
                                                <CameraIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                        startIcon={
                            saving ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : undefined
                        }
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <BarcodeScannerDialog
                open={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onScan={(scanned) => {
                    setBarcode(scanned);
                    setScannerOpen(false);
                }}
                title="Scan Product Barcode"
            />
        </>
    );
};
